import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Target, Calendar, Bot } from "lucide-react";
import FloatingShapes from "./FloatingShapes";
import { useScheduleCall } from "../context/ScheduleCallContext";
const LOGO_SRC = "/photos/purposeLogo.png";

const Hero = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const { openScheduleCall } = useScheduleCall();

  const scrollTo = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const stats = [
    { value: "5+", label: "Live Products Built" },
    { value: "100%", label: "Client Satisfaction" },
    { value: "24/7", label: "AI Agent Uptime" },
    { value: "15+", label: "Years Experience" },
  ];
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-dark-400 bg-mesh-gradient pt-24 pb-16"
    >
      <FloatingShapes />

      <div className="section-container relative z-10 w-full">
        <motion.div
          ref={ref}
          className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Left: Copy */}
          <div className="text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-500/30 bg-primary-500/10 text-primary-300 text-sm font-medium mb-6"
            >
              <span className="w-2 h-2 bg-accent-emerald rounded-full animate-pulse" />
              IT & Research Services
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-4 mb-6"
            >
              <img
                src={LOGO_SRC}
                alt="Purpose Labs — IT and research services logo"
                className="h-20 sm:h-24 w-auto"
              />
              <span className="font-display text-2xl font-bold text-white">
                Purpose Labs
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] mb-6"
            >
              We Build{" "}
              <span className="gradient-text">Revenue-Generating</span>{" "}
              Digital Products That Drive Results
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8 max-w-xl"
            >
              Purpose Labs delivers IT solutions and research-driven products worldwide —
              lead generation websites, booking platforms, custom CRMs, and AI
              inbound call agents built to grow your business.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 mb-12"
            >
              <motion.button
                onClick={openScheduleCall}
                className="btn-primary text-lg"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                Schedule a Call →
              </motion.button>
              <motion.button
                onClick={() => scrollTo("#portfolio")}
                className="btn-outline text-lg"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                View Our Work
              </motion.button>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-4"
            >
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="glass-panel px-4 py-3 text-center"
                >
                  <div className="font-display text-2xl font-bold text-white">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: 3D service cards stack */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative hidden lg:block perspective-1500 preserve-3d h-[520px]"
          >
            {[
              {
                title: "Lead Gen Websites",
                desc: "High-converting landing pages that capture and qualify leads 24/7",
                icon: Target,
                color: "from-primary-600/20 to-primary-400/10",
                border: "border-primary-500/30",
                z: 60,
                rotate: -6,
                top: "0%",
              },
              {
                title: "Booking Platforms",
                desc: "Real-time reservation systems for homestays, hotels & services",
                icon: Calendar,
                color: "from-accent-cyan/20 to-accent-cyan/5",
                border: "border-accent-cyan/30",
                z: 40,
                rotate: 3,
                top: "28%",
              },
              {
                title: "AI Call Agents",
                desc: "Inbound AI that answers calls, qualifies leads & books appointments",
                icon: Bot,
                color: "from-accent-violet/20 to-accent-violet/5",
                border: "border-accent-violet/30",
                z: 20,
                rotate: -2,
                top: "56%",
              },
            ].map((card, i) => {
              const CardIcon = card.icon;
              return (
              <motion.div
                key={i}
                className={`absolute left-0 right-0 glass-panel p-6 bg-gradient-to-br ${card.color} ${card.border}`}
                style={{
                  top: card.top,
                  transform: `translateZ(${card.z}px) rotateX(5deg) rotateY(${card.rotate}deg)`,
                  transformStyle: "preserve-3d",
                }}
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 5 + i,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.8,
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="text-primary-400 flex-shrink-0">
                    <CardIcon className="w-8 h-8" strokeWidth={1.75} aria-hidden />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white mb-1">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-400">{card.desc}</p>
                  </div>
                </div>
              </motion.div>
            );
            })}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-5 h-9 border-2 border-white/20 rounded-full flex justify-center pt-2">
          <motion.div
            className="w-1 h-2 bg-primary-400 rounded-full"
            animate={{ opacity: [1, 0.3, 1], y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
